import { parseMailDate } from './mailDates';
import { isContactUnsubscribed, isContactBounced } from './contactUnsubscribe';

export const parseDate = parseMailDate;

export const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export const CAMPAIGN_START_COLOR = '#ea580c';
export const CAMPAIGN_FOLLOWUP_COLOR = '#0369a1';
/** @deprecated Alias – gleiche Farbe wie Kampagnen-Start */
export const CAMPAIGN_BATCH_START_COLOR = CAMPAIGN_START_COLOR;
/** @deprecated Alias – gleiche Farbe wie Follow-up */
export const CAMPAIGN_BATCH_FOLLOWUP_COLOR = CAMPAIGN_FOLLOWUP_COLOR;
export const CAMPAIGN_START_LABEL = 'Kampagnen-Start';
export const CAMPAIGN_FOLLOWUP_LABEL = 'Follow-up';
export const CAMPAIGN_BATCH_START_LABEL = CAMPAIGN_START_LABEL;
export const CAMPAIGN_BATCH_FOLLOWUP_LABEL = CAMPAIGN_FOLLOWUP_LABEL;

export const USER_EVENT_COLOR = '#7c3aed';
export const USER_EVENT_LABEL = 'Termin';

/** Farbe für Kalender-Chips / Dots — Legende als Fallback */
export const resolveCalendarEventColor = (ev) => {
  const custom = String(ev?.color || '').trim();
  if (ev?.source === 'campaign') {
    if (custom) return custom;
    return ev.kind === 'followup' ? CAMPAIGN_FOLLOWUP_COLOR : CAMPAIGN_START_COLOR;
  }
  // Alte Default-Blau-Termine → neues Lila
  if (custom && custom.toLowerCase() !== '#2563eb') return custom;
  return USER_EVENT_COLOR;
};

export const eventColorRgba = (hex, alpha = 0.16) => {
  const raw = String(hex || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) {
    return `rgba(124, 58, 237, ${alpha})`;
  }
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

/** Montag als Wochenbeginn (lokal). */
export const startOfWeek = (d) => {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const offset = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - offset);
  return date;
};

export const toDateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const formatMonthTitle = (d) =>
  d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

/** Titel für die 3-Wochen-Ansicht (Vorwoche + aktuelle + nächste). */
export const formatThreeWeekTitle = (anchor) => {
  const mid = startOfWeek(anchor);
  const from = new Date(mid);
  from.setDate(from.getDate() - 7);
  const to = new Date(mid);
  to.setDate(to.getDate() + 13);
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    const monthYear = to.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    return `${from.getDate()}.–${to.getDate()}. ${monthYear}`;
  }
  const left = from.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
  const right = to.toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${left} – ${right}`;
};

export const formatEventTime = (value) => {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

export const rangesOverlap = (aStart, aEnd, bStart, bEnd) => {
  const as = aStart instanceof Date ? aStart.getTime() : Number(aStart);
  const ae = aEnd instanceof Date ? aEnd.getTime() : Number(aEnd);
  const bs = bStart instanceof Date ? bStart.getTime() : Number(bStart);
  const be = bEnd instanceof Date ? bEnd.getTime() : Number(bEnd);
  if (![as, ae, bs, be].every(Number.isFinite)) return false;
  return as < be && bs < ae;
};

const resolveEventSeriesKey = (ev) => {
  if (ev?.seriesId != null) return String(ev.seriesId);
  const raw = String(ev?.id ?? '');
  if (raw.includes('::')) return raw.split('::')[0];
  return raw;
};

/**
 * Findet belegte Termine (User-Events), die sich mit dem Zeitraum überschneiden.
 * Kampagnen zählen nicht als Buchungskonflikt.
 */
export const findBusyConflicts = (
  events = [],
  startAt,
  endAt,
  { excludeId = null, allDay = false } = {}
) => {
  if (allDay) return [];
  const start = parseDate(startAt) || (startAt instanceof Date ? startAt : new Date(startAt));
  const end =
    parseDate(endAt) ||
    (endAt instanceof Date ? endAt : endAt ? new Date(endAt) : null) ||
    new Date(start.getTime() + 60 * 60 * 1000);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const excludeKey = excludeId != null ? String(excludeId) : null;

  return (events || []).filter((ev) => {
    if (!ev || ev.source === 'campaign' || ev.allDay) return false;
    if (excludeKey) {
      const key = resolveEventSeriesKey(ev);
      if (key === excludeKey || String(ev.id) === excludeKey) return false;
    }
    const evStart = parseDate(ev.startAt);
    if (!evStart) return false;
    const evEnd =
      parseDate(ev.endAt) ||
      new Date(
        evStart.getTime() +
          Math.max(15, Number(ev.durationMinutes) || 60) * 60 * 1000
      );
    return rangesOverlap(start, end, evStart, evEnd);
  });
};

export const buildMonthCells = (cursor) => {
  const first = startOfMonth(cursor);
  const last = endOfMonth(cursor);
  const startOffset = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startOffset; i += 1) {
    const d = new Date(first);
    d.setDate(d.getDate() - (startOffset - i));
    cells.push({ date: d, inMonth: false });
  }
  for (let day = 1; day <= last.getDate(); day += 1) {
    cells.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), day), inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const d = new Date(cells[cells.length - 1].date);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, inMonth: false });
  }
  return cells;
};

/**
 * Drei Wochen um den Anker: Vorwoche, aktuelle Woche, Folgewoche.
 * `inMonth` markiert die mittlere (fokussierte) Woche für die Darstellung.
 */
export const buildThreeWeekCells = (anchor) => {
  const midWeekStart = startOfWeek(anchor);
  const start = new Date(midWeekStart);
  start.setDate(start.getDate() - 7);
  const cells = [];
  for (let i = 0; i < 21; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({ date: d, inMonth: i >= 7 && i < 14 });
  }
  return cells;
};

/** Fetch-Fenster für Monat- bzw. 3-Wochen-Ansicht. */
export const getCalendarFetchRange = (cursor, { threeWeeks = false } = {}) => {
  if (threeWeeks) {
    const mid = startOfWeek(cursor);
    const from = new Date(mid);
    from.setDate(from.getDate() - 14);
    const to = new Date(mid);
    to.setDate(to.getDate() + 21);
    return { from, to };
  }
  const from = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  from.setDate(from.getDate() - 7);
  const to = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 7);
  return { from, to };
};

export const groupEventsByDay = (events = []) => {
  const map = {};
  events.forEach((ev) => {
    const start = parseDate(ev.startAt);
    if (!start) return;
    const end = parseDate(ev.endAt) || start;
    const cursorDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    // Ende exklusiv, wenn es auf Mitternacht fällt (üblich bei Ganztägig)
    // oder genau +24h dieselbe Uhrzeit (Start 09:00 → Ende Folgetag 09:00).
    const endAfterStart = end.getTime() > start.getTime();
    const endIsMidnight =
      endAfterStart &&
      end.getHours() === 0 &&
      end.getMinutes() === 0 &&
      end.getSeconds() === 0 &&
      end.getMilliseconds() === 0;
    const endIsSameClockNextDays =
      endAfterStart &&
      end.getHours() === start.getHours() &&
      end.getMinutes() === start.getMinutes() &&
      end.getSeconds() === start.getSeconds() &&
      last > cursorDay;
    if (endIsMidnight || (ev.allDay && endIsSameClockNextDays)) {
      last = new Date(last.getFullYear(), last.getMonth(), last.getDate() - 1);
    }

    // Ganztägig ohne echte Mehrtages-Dauer → nur Starttag
    if (ev.allDay) {
      const durationMin = Number(ev.durationMinutes);
      if (!Number.isFinite(durationMin) || durationMin <= 1440) {
        last = new Date(cursorDay);
      }
    }

    if (last < cursorDay) last = new Date(cursorDay);

    while (cursorDay <= last) {
      const key = toDateKey(cursorDay);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
      cursorDay.setDate(cursorDay.getDate() + 1);
    }
  });
  return map;
};

const contactLabel = (campaign) =>
  campaign.contact_name || campaign.contact_email || `Kampagne #${campaign.id}`;

const memberSnapshot = (campaign) => ({
  id: Number(campaign.id),
  contact_name: campaign.contact_name || '',
  contact_email: campaign.contact_email || '',
  contact_company: campaign.contact_company || '',
  status: campaign.status || '',
  reply_received: Number(campaign.reply_received) === 1,
  auto_reply_received: Number(campaign.auto_reply_received) === 1,
  unsubscribed: isContactUnsubscribed(campaign) ? 1 : 0,
  bounced: isContactBounced(campaign) ? 1 : 0,
  template_name: campaign.template_name || '',
  follow_up_count: Number(campaign.follow_up_count) || 0,
});

const isStepEnabled = (step) =>
  !(step?.enabled === 0 || step?.enabled === false || step?.enabled === '0');

/** Noch aktive Empfänger für Follow-up-Termine (kein Versand an Abmelder / Antworten / Pause). */
export const isCampaignFollowUpEligible = (campaign) => {
  if (!campaign) return false;
  if (isContactUnsubscribed(campaign) || isContactBounced(campaign)) return false;
  if (Number(campaign.reply_received) === 1) return false;
  const status = String(campaign.status || '').toLowerCase();
  if (
    status === 'unsubscribed' ||
    status === 'cancelled' ||
    status === 'deleted' ||
    status === 'bounced' ||
    status === 'completed' ||
    status === 'archived' ||
    status === 'paused'
  ) {
    return false;
  }
  // Nur laufende / geplante Kampagnen
  if (status !== 'sent' && status !== 'scheduled' && status !== 'sending') return false;

  // Keine aktivierten Follow-up-Schritte → nichts anzeigen
  const steps = Array.isArray(campaign.followups) ? campaign.followups : [];
  const sentCount = Number(campaign.follow_up_count) || 0;
  const hasPendingEnabled = steps.some(
    (s) => isStepEnabled(s) && Number(s.follow_up_number) > sentCount
  );
  if (!hasPendingEnabled) return false;

  // Status sent ohne next_follow_up: nur absolute Termine möglich (collectFollowupTimes)
  return true;
};

/** Pausierte Kampagnen erscheinen nicht im Kalender (weder Start noch Follow-up). */
const campaignShowsCalendarStart = (campaign) =>
  String(campaign?.status || '').toLowerCase() !== 'paused';

/** Empfänger-Liste in Kalender-Details: Abmelder / Antworten ausblenden. */
export const filterCalendarEventMembers = (members = [], { kind } = {}) => {
  const list = Array.isArray(members) ? members : [];
  if (kind === 'followup') {
    return list.filter((m) => isCampaignFollowUpEligible(m));
  }
  // Start-Termine: Abmelder ausblenden (Antworten historisch ok, aber nicht als „offen“ wirken)
  return list.filter((m) => !isContactUnsubscribed(m) && !isContactBounced(m) && String(m.status || '').toLowerCase() !== 'unsubscribed' && String(m.status || '').toLowerCase() !== 'bounced');
};

const formatRecipientSummary = (members, maxNames = 2) => {
  const names = members.map(contactLabel).filter(Boolean);
  if (!names.length) return `${members.length} Empfänger`;
  if (names.length <= maxNames) return names.join(', ');
  return `${names.slice(0, maxNames).join(', ')} +${names.length - maxNames}`;
};

const addDays = (date, days) => {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + Math.max(1, Number(days) || 1));
  return d;
};

const minuteKey = (date) => {
  if (!date) return '';
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
};

const partitionCampaigns = (campaigns = []) => {
  const batches = new Map();
  const singles = [];

  for (const c of campaigns) {
    const status = String(c.status || '').toLowerCase();
    if (status === 'cancelled' || status === 'deleted' || status === 'archived') continue;
    const bid = c.batch_id != null && String(c.batch_id).trim() ? String(c.batch_id) : null;
    if (!bid) {
      singles.push(c);
      continue;
    }
    if (!batches.has(bid)) batches.set(bid, []);
    batches.get(bid).push(c);
  }

  // Ohne batch_id: gleicher Start (Minute) + gleiches Template → Gruppe
  const soft = new Map();
  const remainingSingles = [];
  for (const c of singles) {
    const start = parseDate(c.sent_at || c.created_at);
    const templateKey = c.template_id != null ? String(c.template_id) : (c.template_name || '');
    if (!start || !templateKey) {
      remainingSingles.push(c);
      continue;
    }
    const key = `soft:${templateKey}:${minuteKey(start)}`;
    if (!soft.has(key)) soft.set(key, []);
    soft.get(key).push(c);
  }

  const groups = [];
  for (const [batchId, members] of batches.entries()) {
    if (members.length >= 2) {
      groups.push({ type: 'batch', batchId, members });
    } else {
      remainingSingles.push(...members);
    }
  }
  for (const [softId, members] of soft.entries()) {
    if (members.length >= 2) {
      groups.push({ type: 'batch', batchId: softId, members });
    } else {
      remainingSingles.push(...members);
    }
  }
  for (const c of remainingSingles) {
    groups.push({ type: 'single', batchId: null, members: [c] });
  }
  return groups;
};

const collectFollowupTimes = (campaign, startDate) => {
  const status = String(campaign.status || '').toLowerCase();
  const times = []; // { n, at, step, status }

  // Keine Follow-up-Termine für Abmelder / Antworten / Pause / beendet
  if (!isCampaignFollowUpEligible(campaign)) return times;

  if (status !== 'sent' && status !== 'scheduled' && status !== 'sending') {
    return times;
  }

  const sentCount = Number(campaign.follow_up_count) || 0;
  const steps = [...(campaign.followups || [])]
    .filter(isStepEnabled)
    .sort((a, b) => Number(a.follow_up_number) - Number(b.follow_up_number));

  // Keine aktivierten Follow-ups → nichts im Kalender (auch nicht next_follow_up-Rest)
  const remaining = steps.filter((s) => Number(s.follow_up_number) > sentCount);
  if (!remaining.length) return times;

  const serverNextAt = parseDate(campaign.next_follow_up);
  const nextNum =
    campaign.next_followup_number != null
      ? Number(campaign.next_followup_number)
      : remaining[0]
        ? Number(remaining[0].follow_up_number)
        : sentCount + 1;

  // Relativ: ohne serverseitigen next_follow_up gibt es keinen geplanten Versand
  const hasRelativePending = remaining.some(
    (s) => String(s.schedule_mode || 'relative').toLowerCase() !== 'absolute'
  );
  if (hasRelativePending && !serverNextAt && status === 'sent') {
    // Nur noch absolute Termine anzeigen, falls vorhanden
    for (const step of remaining) {
      if (String(step.schedule_mode || '').toLowerCase() !== 'absolute') continue;
      const at = parseDate(step.scheduled_at);
      if (!at) continue;
      times.push({ n: Number(step.follow_up_number), at, step, status });
    }
    return times;
  }

  let cursor = startDate ? new Date(startDate.getTime()) : null;
  if (serverNextAt) cursor = new Date(serverNextAt.getTime());

  for (const step of remaining) {
    const n = Number(step.follow_up_number);
    const mode = String(step.schedule_mode || 'relative').toLowerCase();
    let at = null;

    if (mode === 'absolute') {
      at = parseDate(step.scheduled_at);
    } else if (n === nextNum && serverNextAt) {
      at = serverNextAt;
    } else if (cursor) {
      at = addDays(cursor, step.days_after_previous);
    }

    if (at) cursor = at;
    if (!at) continue;

    times.push({ n, at, step, status });
  }

  return times;
};

const pushSingleCampaignEvents = (c, out) => {
  const who = contactLabel(c);
  const status = String(c.status || '').toLowerCase();
  const startRaw =
    status === 'scheduled' || status === 'sending'
      ? (c.scheduled_at || c.created_at)
      : (c.sent_at || c.created_at);
  const startDate = parseDate(startRaw);
  const campaignLabel = c.template_name || who;

  if (startDate && campaignShowsCalendarStart(c)) {
    out.push({
      id: `campaign-${c.id}-start`,
      source: 'campaign',
      campaignId: Number(c.id),
      campaignIds: [Number(c.id)],
      batchId: c.batch_id ? String(c.batch_id) : null,
      isBatch: false,
      members: [memberSnapshot(c)],
      kind: 'start',
      templateName: c.template_name || '',
      campaignLabel,
      title: `Kampagne: ${who}`,
      description: [
        c.template_name ? `Kampagne / Vorlage: ${c.template_name}` : 'Kampagne ohne Vorlagenname',
        `Status: ${c.status || '—'}`,
        `Kampagne #${c.id}`,
      ].filter(Boolean).join('\n'),
      location: '',
      startAt: startDate.toISOString(),
      endAt: null,
      allDay: false,
      color: CAMPAIGN_START_COLOR,
    });
  }

  for (const fu of collectFollowupTimes(c, startDate)) {
    const mode = String(fu.step?.schedule_mode || 'relative').toLowerCase();
    const fuTemplate = fu.step?.template_name || '';
    out.push({
      id: `campaign-${c.id}-fu-${fu.n}`,
      source: 'campaign',
      campaignId: Number(c.id),
      campaignIds: [Number(c.id)],
      batchId: c.batch_id ? String(c.batch_id) : null,
      isBatch: false,
      members: [memberSnapshot(c)],
      kind: 'followup',
      followupNumber: fu.n,
      templateName: c.template_name || '',
      followupTemplateName: fuTemplate,
      campaignLabel,
      title: `Follow-up ${fu.n}: ${who}`,
      description: [
        c.template_name ? `Kampagne / Vorlage: ${c.template_name}` : null,
        fuTemplate ? `Follow-up-Vorlage: ${fuTemplate}` : 'Geplantes Follow-up',
        fu.step
          ? (mode === 'absolute' ? 'Fester Termin' : `+${Math.max(1, Number(fu.step.days_after_previous) || 1)} Tage`)
          : null,
        `Kampagne #${c.id}`,
      ].filter(Boolean).join('\n'),
      location: '',
      startAt: fu.at.toISOString(),
      endAt: null,
      allDay: false,
      color: CAMPAIGN_FOLLOWUP_COLOR,
    });
  }
};

const pushBatchCampaignEvents = (batchId, members, out) => {
  const sorted = [...members].sort((a, b) => Number(a.id) - Number(b.id));
  const head = sorted[0];
  const startMembers = sorted.filter(
    (m) =>
      campaignShowsCalendarStart(m) &&
      !isContactUnsubscribed(m) &&
      !isContactBounced(m) &&
      String(m.status || '').toLowerCase() !== 'unsubscribed' &&
      String(m.status || '').toLowerCase() !== 'bounced'
  );
  const snapshots = (startMembers.length ? startMembers : sorted).map(memberSnapshot);
  const templateName = sorted.map((m) => m.template_name).find(Boolean) || '';
  const recipientSummary = formatRecipientSummary(startMembers.length ? startMembers : sorted);

  const startDates = sorted
    .filter(campaignShowsCalendarStart)
    .map((m) => {
      const st = String(m.status || '').toLowerCase();
      return parseDate(
        st === 'scheduled' || st === 'sending'
          ? (m.scheduled_at || m.created_at)
          : (m.sent_at || m.created_at)
      );
    })
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime());
  const startDate = startDates[0] || null;

  if (startDate) {
    out.push({
      id: `campaign-batch-${batchId}-start`,
      source: 'campaign',
      campaignId: Number(head.id),
      campaignIds: sorted.map((m) => Number(m.id)),
      batchId: String(batchId),
      isBatch: true,
      members: snapshots,
      kind: 'start',
      templateName: templateName || '',
      campaignLabel: templateName || 'Kampagne',
      title: templateName || 'Kampagne',
      description: [`Kampagne: ${templateName || 'Ohne Vorlage'}`, recipientSummary]
        .filter(Boolean)
        .join('\n'),
      location: '',
      startAt: startDate.toISOString(),
      endAt: null,
      allDay: false,
      color: CAMPAIGN_BATCH_START_COLOR,
    });
  }

  // Follow-ups: gleiches Follow-up + gleiche Minute → ein Gruppeneintrag
  // Nur Empfänger, die das Follow-up noch bekommen würden
  const buckets = new Map();
  for (const m of sorted) {
    if (!isCampaignFollowUpEligible(m)) continue;
    const st = String(m.status || '').toLowerCase();
    const mStart = parseDate(
      st === 'scheduled' || st === 'sending'
        ? (m.scheduled_at || m.created_at)
        : (m.sent_at || m.created_at)
    );
    for (const fu of collectFollowupTimes(m, mStart)) {
      const key = `${fu.n}|${minuteKey(fu.at)}`;
      if (!buckets.has(key)) {
        buckets.set(key, {
          n: fu.n,
          at: fu.at,
          step: fu.step,
          members: [],
        });
      }
      const bucket = buckets.get(key);
      bucket.members.push(m);
      if (!bucket.step && fu.step) bucket.step = fu.step;
    }
  }

  for (const bucket of buckets.values()) {
    if (!bucket.members.length) continue;
    const names = formatRecipientSummary(bucket.members);
    const baseCampaignLabel = templateName || 'Kampagne';
    const fuTemplate = String(bucket.step?.template_name || '').trim();
    out.push({
      id: `campaign-batch-${batchId}-fu-${bucket.n}-${minuteKey(bucket.at)}`,
      source: 'campaign',
      campaignId: Number(bucket.members[0].id),
      campaignIds: bucket.members.map((m) => Number(m.id)),
      batchId: String(batchId),
      isBatch: true,
      members: bucket.members.map(memberSnapshot),
      kind: 'followup',
      followupNumber: bucket.n,
      templateName: baseCampaignLabel,
      followupTemplateName: fuTemplate || '',
      campaignLabel: baseCampaignLabel,
      title: `Follow-up ${bucket.n} · ${fuTemplate || baseCampaignLabel}`,
      description: [
        `Kampagne: ${baseCampaignLabel}`,
        names,
        fuTemplate && fuTemplate !== baseCampaignLabel
          ? `Follow-up-Vorlage: ${fuTemplate}`
          : null,
      ]
        .filter(Boolean)
        .join('\n'),
      location: '',
      startAt: bucket.at.toISOString(),
      endAt: null,
      allDay: false,
      color: CAMPAIGN_BATCH_FOLLOWUP_COLOR,
    });
  }
};

/**
 * Kampagnen → Kalender-Einträge
 * - Einzelkampagnen: Start + Follow-ups pro Kontakt
 * - batch_id mit ≥2 Empfängern: ein Gruppen-Eintrag (statt N Einzelkarten)
 */
export const campaignsToCalendarEvents = (campaigns = []) => {
  const out = [];
  const groups = partitionCampaigns(campaigns);

  for (const group of groups) {
    if (group.type === 'batch') {
      pushBatchCampaignEvents(group.batchId, group.members, out);
    } else {
      pushSingleCampaignEvents(group.members[0], out);
    }
  }

  return out;
};
